import useUser from "@cabukaka/hooks/src/users/useUser"
import useCustomParams from "../../../hooks/params/useCustomParams"
import PageLayout from "../../../layouts/PageLayout"
import PageContainer from "../../../components/ui/PageContainer"
import { User, Mail, Shield, Calendar, CheckCircle, XCircle, Trash2, ArrowLeft, Check } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { alertSuccess, alertError } from "../../../helpers/alertError"
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal"

export default function UserDetailsPage() {
  const { id } = useCustomParams()
  const navigate = useNavigate()
  const { user, deleteUser, verifyUser } = useUser({ id: id! })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin":
        return <span className="px-3 py-1 text-sm rounded-full bg-purple-100 text-purple-700 font-medium">Administrateur</span>
      case "shop":
        return <span className="px-3 py-1 text-sm rounded-full bg-orange-100 text-orange-700 font-medium">Vendeur</span>
      case "client":
        return <span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700 font-medium">Client</span>
      default:
        return <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700 font-medium">{role}</span>
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    if (!id) {
      alertError("Erreur lors de la suppression")
      return
    }
    try {
      await deleteUser(id)
      alertSuccess("Utilisateur supprimé avec succès")
      navigate(-1)
    } catch (error) {
      alertError("Erreur lors de la suppression")
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const handleVerify = async () => {
    if (!id) return
    setIsVerifying(true)
    try {
      await verifyUser()
      alertSuccess("Utilisateur vérifié avec succès")
    } catch (error) {
      alertError("Erreur lors de la vérification")
    } finally {
      setIsVerifying(false)
    }
  }

  if (!user) {
    return (
      <PageLayout title="Détails de l'utilisateur">
        <PageContainer>
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        </PageContainer>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Détails de l'utilisateur"
      actions={
        <div className="flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            <ArrowLeft size={18} /> Retour
          </button>
        
          {!user.isVerified && (
            <button
              onClick={handleVerify}
              disabled={isVerifying}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
            >
              <Check size={18} />
              {isVerifying ? "Vérification..." : "Vérifier"}
            </button>
          )}
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <Trash2 size={18} /> Supprimer
          </button>
        </div>
      }
    >
      <PageContainer>
        <div className="space-y-6">
          {/* En-tête avec avatar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.username} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-primary" />
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{user.username}</h2>
                <div className="flex items-center gap-3 mt-2">
                  {getRoleBadge(user.role)}
                  {user.isVerified ? (
                    <span className="flex items-center gap-1 text-emerald-600 text-sm">
                      <CheckCircle size={16} /> Vérifié
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600 text-sm">
                      <XCircle size={16} /> Non vérifié
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Informations générales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Coordonnées
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="text-gray-900">{user.email || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Téléphone</span>
                  <span className="text-gray-900">{user?.profile?.phone  || "-"}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Informations de compte
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Statut</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${user.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {user.status || "Actif"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fournisseur</span>
                  <span className="text-gray-900">{user.provider || "Email"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Activité
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Date d'inscription</span>
                <span className="text-gray-900">{new Date(user.createdAt).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Dernière connexion</span>
                <span className="text-gray-900">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('fr-FR') : "-"}</span>
              </div>
            </div>
          </div>

          {/* Profil associé (client/shop/admin) */}
          {user.profile && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Profil associé</h3>
              <pre className="text-sm text-gray-600 overflow-x-auto">
                {JSON.stringify(user.profile, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Supprimer l'utilisateur"
          message={`Supprimer "${user.username}" ? Cette action est irréversible.`}
          confirmText={isDeleting ? "Suppression..." : "Supprimer"}
        />
      </PageContainer>
    </PageLayout>
  )
}